import { PriceManager } from 'prices'
import { Currency } from 'prices/types'
import { isPodCoverVariantId } from 'products/utils'
import Stripe from 'stripe'

interface CheckoutEmailData {
	items: Item[]
}

interface Item {
	title?: string
	quantity: number
	price: string
	is_bundle: boolean
	image_url: string
	size?: string
	bundle_items?: BundleItem[]
}

interface BundleItem {
	title: string
	quantity: number
	size?: string
}

interface PseudoInvoice {
	lines: {
		data: PseudoInvoiceLineItem[]
	}
}

interface PseudoInvoiceLineItem {
	price: {
		id: string
		product?: {
			images?: string[]
		}
		price: string
	}
	description?: string
	quantity?: number
	metadata?: {
		size?: string
	}
}

export const invoiceToPseudoInvoice = (invoice: Stripe.Invoice | Stripe.UpcomingInvoice): PseudoInvoice => {
	const currency = invoice.currency.toUpperCase() as Currency
	return {
		...invoice,
		lines: {
			data: invoice.lines.data.flatMap((item) => {
				return {
					price: {
						id: item.price?.id ?? '',
						product: {
							images: (item.price?.product as Stripe.Product).images ?? [],
						},
						price: PriceManager.formatPriceToCurrency((item.price?.unit_amount ?? 0) / 100, currency ?? 'USD'),
						currency,
					},
					description: item.description ?? undefined,
					quantity: item.quantity ?? 1,
					metadata: item.metadata ?? undefined,
				}
			}),
		},
	}
}

export const formatCheckoutData = (invoice: PseudoInvoice): CheckoutEmailData => {
	return {
		items: invoice.lines.data.flatMap((item) => formatLineItem(item)),
	}
}

export const formatLineItem = (item: PseudoInvoiceLineItem, includeBundleItems = true): Item => {
	const bundleItems = includeBundleItems ? getBundleItems(item) : []

	return {
		title: item.description ?? undefined,
		quantity: item.quantity ?? 1,
		price: item.price.price,
		size: item.metadata?.size ?? undefined,
		is_bundle: bundleItems.length > 0,
		bundle_items: bundleItems,
		image_url: item.price.product?.images?.[0] ?? '',
	}
}

const getBundleItems = (item: PseudoInvoiceLineItem): BundleItem[] => {
	// TODO: shift this out for Plus variants
	const variantsA = [
		{
			name: 'Queen',
			id: 'price_1RKJ9CDptqM2bn1xBYSLdaLu',
		},
		{
			name: 'King',
			id: 'price_1RKJ9GDptqM2bn1xm3a0SHL4',
		},
		{
			name: 'Cali King',
			id: 'price_1RKJ9KDptqM2bn1xQikbW6L9',
		},
		{
			name: 'CA Queen',
			id: 'price_1RKJ9uDptqM2bn1xtUi4YXzx',
		},
		{
			name: 'CA King',
			id: 'price_1RKJ9xDptqM2bn1xoULWTLv4',
		},
		{
			name: 'EU Super King',
			id: 'price_1RLMEADptqM2bn1x3yrsr6gO',
		},
		{
			name: 'EU Emperor',
			id: 'price_1RLMEDDptqM2bn1xfMwdB3fN',
		},
		{
			name: 'UK Super King',
			id: 'price_1RKJAnDptqM2bn1x2MyKwdjH',
		},
		{
			name: 'UK Emperor',
			id: 'price_1RKJAqDptqM2bn1x03Qz4H8w',
		},
		{
			name: 'AU Queen',
			id: 'price_1RKMOqDptqM2bn1xhZTmjbZq',
		},
		{
			name: 'AU King',
			id: 'price_1RKMOuDptqM2bn1xqa1OJ2nN',
		},
		{
			name: 'SE Super King',
			id: 'price_1RKJCODptqM2bn1xm87TQ3Vy',
		},
		{
			name: 'SE Emperor',
			id: 'price_1RKJCRDptqM2bn1xGpnx5OV5',
		},
		{
			name: 'DK Super King',
			id: 'price_1RKJCqDptqM2bn1xWXExxITG',
		},
		{
			name: 'DK Emperor',
			id: 'price_1RKJCtDptqM2bn1x6CDTNodW',
		},
		{
			name: 'CH Super King',
			id: 'price_1RKJDHDptqM2bn1xmXFHr51N',
		},
		{
			name: 'CH Emperor',
			id: 'price_1RKJDKDptqM2bn1xnKFD21GD',
		},
		{
			name: 'AE King',
			id: 'price_1RLMEyDptqM2bn1xaYWWRHEf',
		},
		{
			name: 'AE Emperor',
			id: 'price_1RLMF1DptqM2bn1xJOD9LauE',
		},
		{
			name: 'SA King',
			id: 'price_1RKJDhDptqM2bn1xBZ7RlTfm',
		},
		{
			name: 'SA Emperor',
			id: 'price_1RKJDjDptqM2bn1xjgVjWreI',
		},
		{
			name: 'MX Queen',
			id: 'price_1RKJByDptqM2bn1xLe5VDTBb',
		},
		{
			name: 'MX King',
			id: 'price_1RKJC0DptqM2bn1xvF6nFtGJ',
		},
		{
			name: 'Queen',
			id: 'price_1RKJ9FDptqM2bn1xfifjrCxx',
		},
		{
			name: 'King',
			id: 'price_1RKJ9IDptqM2bn1xR6urTizK',
		},
		{
			name: 'Cali King',
			id: 'price_1RKJ9MDptqM2bn1xrFBAO1qR',
		},
		{
			name: 'CA Queen',
			id: 'price_1RKJ9wDptqM2bn1xmc2x5cbL',
		},
		{
			name: 'CA King',
			id: 'price_1RKJ9yDptqM2bn1xY31VmYOh',
		},
		{
			name: 'EU Super King',
			id: 'price_1RLMECDptqM2bn1xlBtmUIap',
		},
		{
			name: 'EU Emperor',
			id: 'price_1RLMEFDptqM2bn1xizfH0nxe',
		},
		{
			name: 'UK Super King',
			id: 'price_1RKJApDptqM2bn1xHyFrvAno',
		},
		{
			name: 'UK Emperor',
			id: 'price_1RKJAsDptqM2bn1xyUhgXgew',
		},
		{
			name: 'AU Queen',
			id: 'price_1RKMOtDptqM2bn1xgyz6Lm8j',
		},
		{
			name: 'AU King',
			id: 'price_1RKMOxDptqM2bn1xJDsFBhKo',
		},
		{
			name: 'SE Super King',
			id: 'price_1RKJCQDptqM2bn1xpe9tM8Lk',
		},
		{
			name: 'SE Emperor',
			id: 'price_1RKJCTDptqM2bn1xDfIw8U88',
		},
		{
			name: 'DK Super King',
			id: 'price_1RKJCrDptqM2bn1xogcq0zZd',
		},
		{
			name: 'DK Emperor',
			id: 'price_1RKJCuDptqM2bn1xKVsWiMpn',
		},
		{
			name: 'CH Super King',
			id: 'price_1RKJDJDptqM2bn1xh9yrpiOv',
		},
		{
			name: 'CH Emperor',
			id: 'price_1RKJDMDptqM2bn1xiZvgDhOJ',
		},
		{
			name: 'AE Emperor',
			id: 'price_1RLMF3DptqM2bn1xXXPHoVUj',
		},
		{
			name: 'AE King',
			id: 'price_1RLMF0DptqM2bn1xPICsWFXk',
		},
		{
			name: 'MX Queen',
			id: 'price_1RKJBzDptqM2bn1xLXf9E4lX',
		},
		{
			name: 'MX King',
			id: 'price_1RKJC2DptqM2bn1xdSRqlYHQ',
		},
		{
			name: 'SA Emperor',
			id: 'price_1RMtSIDptqM2bn1xMZPnxebI',
		},
		{
			name: 'SA King',
			id: 'price_1RMtSEDptqM2bn1xv2lRPKc8',
		},
	]

	// This is Ultra variants
	const variantsB = [
		{
			name: 'Queen',
			id: 'price_1RKJ9NDptqM2bn1x6JU10R0b',
		},
		{
			name: 'King',
			id: 'price_1RKJ9QDptqM2bn1x1MjuBRyi',
		},
		{
			name: 'Cali King',
			id: 'price_1RKJ9TDptqM2bn1xaftETjAE',
		},
		{
			name: 'CA Queen',
			id: 'price_1RKJ9zDptqM2bn1xxgdxex8N',
		},
		{
			name: 'CA King',
			id: 'price_1RKJA2DptqM2bn1xovfhs2cH',
		},
		{
			name: 'EU Super King',
			id: 'price_1RLMEHDptqM2bn1xOouJ4RCP',
		},
		{
			name: 'EU Emperor',
			id: 'price_1RLMEKDptqM2bn1xwZtfzws4',
		},
		{
			name: 'UK Super King',
			id: 'price_1RKJAtDptqM2bn1xqmKc59C0',
		},
		{
			name: 'UK Emperor',
			id: 'price_1RKJAwDptqM2bn1x3qfw6DiH',
		},
		{
			name: 'AU Queen',
			id: 'price_1RKMOzDptqM2bn1xX5N6j8DO',
		},
		{
			name: 'AU King',
			id: 'price_1RKMP3DptqM2bn1xMtu2eH1W',
		},
		{
			name: 'SE Super King',
			id: 'price_1RKJCUDptqM2bn1xfP57WNDu',
		},
		{
			name: 'SE Emperor',
			id: 'price_1RKJCXDptqM2bn1xA8iUfcqT',
		},
		{
			name: 'DK Super King',
			id: 'price_1RKJCvDptqM2bn1xm02NOkKw',
		},
		{
			name: 'DK Emperor',
			id: 'price_1RKJCyDptqM2bn1xzcgIpMCy',
		},
		{
			name: 'CH Super King',
			id: 'price_1RKJDNDptqM2bn1xtES9onwM',
		},
		{
			name: 'CH Emperor',
			id: 'price_1RKJDQDptqM2bn1x1gq4Qnh4',
		},
		{
			name: 'AE King',
			id: 'price_1RLMF5DptqM2bn1xjz28eQqV',
		},
		{
			name: 'AE Emperor',
			id: 'price_1RLMF8DptqM2bn1xac0hFG95',
		},
		{
			name: 'SA King',
			id: 'price_1RKJDmDptqM2bn1xBhveWgLp',
		},
		{
			name: 'SA Emperor',
			id: 'price_1RKJDpDptqM2bn1xq0Ka56L2',
		},
		{
			name: 'MX Queen',
			id: 'price_1RKJC3DptqM2bn1xgcDesjmM',
		},
		{
			name: 'MX King',
			id: 'price_1RKJC6DptqM2bn1x0GvjE7r7',
		},
		{
			name: 'Queen',
			id: 'price_1RKJ9PDptqM2bn1xjf4VViMD',
		},
		{
			name: 'King',
			id: 'price_1RKJ9SDptqM2bn1xFU7imAxr',
		},
		{
			name: 'Cali King',
			id: 'price_1RKJ9UDptqM2bn1xJj76mrRv',
		},
		{
			name: 'CA Queen',
			id: 'price_1RKJA1DptqM2bn1xra1hT6GD',
		},
		{
			name: 'CA King',
			id: 'price_1RKJA4DptqM2bn1xCX42V167',
		},
		{
			name: 'EU Super King',
			id: 'price_1RLMEJDptqM2bn1x0YiNgwbS',
		},
		{
			name: 'EU Emperor',
			id: 'price_1RLMEMDptqM2bn1xsqb6vPIG',
		},
		{
			name: 'UK Super King',
			id: 'price_1RKJAvDptqM2bn1xbeF66j5X',
		},
		{
			name: 'UK Emperor',
			id: 'price_1RKJAyDptqM2bn1x7cgMijyF',
		},
		{
			name: 'AU Queen',
			id: 'price_1RKMP2DptqM2bn1xJTsoqoL8',
		},
		{
			name: 'AU King',
			id: 'price_1RKMP7DptqM2bn1xrp4tYs5j',
		},
		{
			name: 'SE Super King',
			id: 'price_1RKJCWDptqM2bn1xIbR5yrsG',
		},
		{
			name: 'SE Emperor',
			id: 'price_1RKJCYDptqM2bn1xspq6EEiX',
		},
		{
			name: 'DK Super King',
			id: 'price_1RKJCxDptqM2bn1xnrhPr7eQ',
		},
		{
			name: 'DK Emperor',
			id: 'price_1RKJD0DptqM2bn1xPtQJiuwZ',
		},
		{
			name: 'CH Super King',
			id: 'price_1RKJDPDptqM2bn1xUruQna2X',
		},
		{
			name: 'CH Emperor',
			id: 'price_1RKJDRDptqM2bn1xaYEcmLA0',
		},
		{
			name: 'AE Emperor',
			id: 'price_1RLMFBDptqM2bn1xGC7xTvwu',
		},
		{
			name: 'AE King',
			id: 'price_1RLMF7DptqM2bn1xh49PVcnp',
		},
		{
			name: 'MX Queen',
			id: 'price_1RKJC5DptqM2bn1xpQ5NIDYz',
		},
		{
			name: 'MX King',
			id: 'price_1RKJC7DptqM2bn1xfxnEII2s',
		},
		{
			name: 'SA Emperor',
			id: 'price_1RMtSRDptqM2bn1xxxwguonu',
		},
		{
			name: 'SA King',
			id: 'price_1RMtSODptqM2bn1xmZDf9hB2',
		},
		{
			name: 'SA Emperor',
			id: 'price_1RMtSPDptqM2bn1xjYOkGTIV',
		},
		{
			name: 'SA King',
			id: 'price_1RMtSLDptqM2bn1xkHhV9S2p',
		},
	]

	// Cheap obfuscation
	const str1 = atob('UG9kIDUgQmxhbmtldA==')
	const str2 = atob('UG9kIDUgVWx0aW1lIEJsaW5rZXQ=')
	const oldHubStr = atob('UG9kIDQgSHVi')
	const hubStr = atob('UG9kIDUgSHVi')
	const oldCoverStr = atob('UG9kIDQgQ292ZXI=')
	const newCoverStr = atob('UG9kIDUgQ292ZXI=')

	const isPodCoverProduct = isPodCoverVariantId(item.price.id)

	if (variantsA.some((v) => v.id === item.price.id)) {
		return [
			{
				title: newCoverStr,
				quantity: item.quantity ?? 1,
			},
			{
				title: hubStr,
				quantity: item.quantity ?? 1,
			},
			{
				title: str1,
				quantity: item.quantity ?? 1,
				size: item.metadata?.size ?? undefined,
			},
		]
	}

	if (variantsB.some((v) => v.id === item.price.id)) {
		return [
			{
				title: newCoverStr,
				quantity: item.quantity ?? 1,
			},
			{
				title: hubStr,
				quantity: item.quantity ?? 1,
			},
			{
				title: str1,
				quantity: item.quantity ?? 1,
				size: item.metadata?.size ?? undefined,
			},
			{
				title: str2,
				quantity: item.quantity ?? 1,
				size: item.metadata?.size ?? undefined,
			},
		]
	}

	if (isPodCoverProduct) {
		if (item.description?.includes('5')) {
			return [
				{
					title: newCoverStr,
					quantity: item.quantity ?? 1,
				},
				{
					title: hubStr,
					quantity: item.quantity ?? 1,
				},
			]
		}

		return [
			{
				title: oldCoverStr,
				quantity: item.quantity ?? 1,
			},
			{
				title: oldHubStr,
				quantity: item.quantity ?? 1,
			},
		]
	}

	return []
}
